import React, { useRef, useState } from "react";
import confetti from "canvas-confetti";
import logo from "../../assets/images/logo.png";

const ClassicSpinWheel = () => {
  const spinnerRef = useRef(null);
  const tickerRef = useRef(null);
  const tickerAnimRef = useRef(null);

  const [rotation, setRotation] = useState(25);
  const [spinning, setSpinning] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [currentSlice, setCurrentSlice] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [formErrors, setFormErrors] = useState({ name: "", email: "", phone: "" });

  /* =======================
     HANDLE INPUT CHANGE
     ✅ Clears the error for that field as soon as user starts retyping
  ======================== */
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // ✅ Clear error immediately on change so user can retype freely
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  /* =======================
     HANDLE FORM SUBMIT
  ======================== */
  const handleFormSubmit = () => {
    let errors = { name: "", email: "", phone: "" };
    let valid = true;

    if (!formData.name.trim()) {
      errors.name = "Name is required";
      valid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = "Email is required";
      valid = false;
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Invalid email format";
      valid = false;
    }

    const phoneRegex = /^\d{11}$/;
    if (!formData.phone.trim()) {
      errors.phone = "Phone is required";
      valid = false;
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone = "Phone must be 11 digits";
      valid = false;
    }

    setFormErrors(errors);

    if (valid) {
      setFormErrors({ name: "", email: "", phone: "" });
      submitToNetlify();
      startSpin();
    }
  };

  /* =======================
     NETLIFY SILENT SUBMIT
  ======================== */
  const submitToNetlify = () => {
    const formBody = new URLSearchParams({
      "form-name": "spin-wheel",
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
    });

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: formBody.toString(),
    }).catch(() => {});
  };

  /* =======================
     PRIZES
  ======================== */
  const prizes = [
    { text: "Free GuestPhone", color: "hsl(197 30% 43%)" },
    { text: "Free Wooden Frame", color: "hsl(173 58% 39%)" },
    { text: "Free ¼ hour", color: "hsl(43 74% 66%)" },
    { text: "Try Again", color: "hsl(350 60% 52%)" },
    { text: "Hard Luck", color: "hsl(27 87% 67%)" },
    { text: "Free GuestPhone", color: "hsl(12 76% 61%)" },
    { text: "Try Again", color: "hsl(91 43% 54%)" },
    { text: "Hard Luck", color: "hsl(140 36% 74%)" },
  ];

  const prizeSlice = 360 / prizes.length;
  const prizeOffset = Math.floor(180 / prizes.length);

  const spinertia = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  const fireConfetti = () => {
    confetti({
      particleCount: 120,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#3b82f6", "#60a5fa", "#93c5fd", "#1e40af"],
    });
  };

  const sendToGoogleSheets = async (prize) => {
    const body = new URLSearchParams({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      prize,
      createdAt: new Date().toISOString(),
    });

    await fetch(
      "https://script.google.com/macros/s/AKfycbxqlSyNvZJS-qzuvLDTlTZAAtaJNOewwaRGUNtVtaKLqLP6-Yh3VdA6KfCEny_MhsgfrQ/exec",
      {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      }
    );
  };

  const runTickerAnimation = () => {
    if (!spinnerRef.current) return;
    const transform = window.getComputedStyle(spinnerRef.current).transform;
    if (transform === "none") return;
    const values = transform.split("(")[1].split(")")[0].split(",");
    const a = parseFloat(values[0]);
    const b = parseFloat(values[1]);
    let rad = Math.atan2(b, a);
    if (rad < 0) rad += 2 * Math.PI;
    const angle = Math.round(rad * (180 / Math.PI));
    const slice = Math.floor(angle / prizeSlice);
    if (slice !== currentSlice) {
      setCurrentSlice(slice);
      tickerRef.current.style.animation = "none";
      setTimeout(() => (tickerRef.current.style.animation = ""), 10);
    }
    tickerAnimRef.current = requestAnimationFrame(runTickerAnimation);
  };

  const startSpin = () => {
    setSpinning(true);
    setShowForm(false);
    setShowResult(false);
    setSelectedPrize(null);
    const newRotation = Math.floor(Math.random() * 360) + spinertia(2000, 5000);
    setRotation(newRotation);
    runTickerAnimation();
  };

  const handleTransitionEnd = async () => {
    cancelAnimationFrame(tickerAnimRef.current);
    const finalRotation = rotation % 360;
    const prizeIndex = Math.floor(finalRotation / prizeSlice);
    setRotation(finalRotation);
    setSelectedPrize(prizeIndex);
    setSpinning(false);
    const prizeText = prizes[prizeIndex].text;
    if (prizeText === "Try Again") return;
    await sendToGoogleSheets(prizeText);
    fireConfetti();
    setShowResult(true);
  };

  const createConicGradient = () =>
    `conic-gradient(from -90deg, ${prizes
      .map(({ color }, i) => `${color} 0 ${(100 / prizes.length) * (prizes.length - i)}%`)
      .reverse()
      .join(", ")})`;

  return (
    <>
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Girassol&display=swap");
        .deal-wheel-container { --size: clamp(250px, 80vmin, 700px); --lg-hs: 0 3%; --lg-stop: 50%; --lg: linear-gradient( hsl(var(--lg-hs) 0%) 0 var(--lg-stop), hsl(var(--lg-hs) 20%) var(--lg-stop) 100% ); min-height: 100vh; display: grid; place-items: center; background: #00000; }
        .deal-wheel { position: relative; display: grid; grid-gap: calc(var(--size) / 20); align-items: center; grid-template-areas: "spinner" "trigger"; font-family: "Girassol", sans-serif; font-size: calc(var(--size) / 21); line-height: 1; text-transform: lowercase; }
        .deal-wheel > * { grid-area: spinner; }
        .deal-wheel .btn-spin { grid-area: trigger; justify-self: center; }
        .spinner { position: relative; display: grid; align-items: center; grid-template-areas: "spinner"; width: var(--size); height: var(--size); border-radius: 50%; box-shadow: inset 0 0 0 calc(var(--size) / 40) hsl(0deg 0% 0% / 0.06); }
        .spinner.is-spinning { transition: transform 8s cubic-bezier(0.1, -0.01, 0, 1); }
        .spinner * { grid-area: spinner; }
        .prize { position: relative; display: flex; align-items: center; padding: 0 calc(var(--size) / 6) 0 calc(var(--size) / 20); width: 50%; height: 50%; transform-origin: center right; user-select: none; }
        .prize-text { color: white; font-weight: bold; text-shadow: 0 2px 4px rgba(0,0,0,0.3); }
        .prize.selected .prize-text { animation: selected 800ms ease infinite; }
        @keyframes selected { 25% { transform: scale(1.25); } 40% { transform: scale(0.92); } 60% { transform: scale(1.02); } 75% { transform: scale(0.98); } 85% { transform: scale(1); } }
        .cap { --cap-size: calc(var(--size) / 4); position: relative; justify-self: center; width: var(--cap-size); height: var(--cap-size); background: var(--lg); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: calc(var(--cap-size) / 3); font-weight: bold; box-shadow: 0 4px 12px rgba(0,0,0,0.3); z-index: 2; }
        .ticker { position: relative; left: calc(var(--size) / -15); width: calc(var(--size) / 10); height: calc(var(--size) / 20); background: var(--lg); z-index: 1; clip-path: polygon(20% 0, 100% 50%, 20% 100%, 0% 50%); transform-origin: center left; }
        .ticker.is-ticking { animation: tick 700ms cubic-bezier(0.34, 1.56, 0.64, 1); }
        @keyframes tick { 40% { transform: rotate(-12deg); } }
        .btn-spin { color: hsl(0deg 0% 100%); background: var(--lg); border: none; font-family: inherit; font-size: inherit; line-height: inherit; text-transform: inherit; padding: 0.9rem 2rem 1rem; border-radius: 0.25rem; cursor: pointer; transition: opacity 200ms ease-out; }
        .btn-spin:focus { outline-offset: 2px; }
        .btn-spin:active { transform: translateY(1px); }
        .btn-spin:disabled { cursor: progress; opacity: 0.25; }
      `}</style>

      {/* Hidden Netlify form for build-time detection */}
      <form name="spin-wheel" netlify="true" hidden>
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="text" name="phone" />
      </form>

      {/* WHEEL */}
      <div className="deal-wheel-container">
        <div className="deal-wheel">
          <div
            ref={spinnerRef}
            className={`spinner ${spinning ? "is-spinning" : ""}`}
            style={{ background: createConicGradient(), transform: `rotate(${rotation}deg)` }}
            onTransitionEnd={handleTransitionEnd}
          >
            {prizes.map((prize, i) => (
              <div
                key={i}
                className={`prize ${selectedPrize === i ? "selected" : ""}`}
                style={{ transform: `rotate(${prizeSlice * i * -1 - prizeOffset}deg)` }}
              >
                <span className="prize-text">{prize.text}</span>
              </div>
            ))}
          </div>

          <div className="cap">
            <img src={logo} alt="logo" loading="eager" decoding="sync" fetchpriority="high" />
          </div>

          <div ref={tickerRef} className="ticker" />
        </div>

        {!showResult && (
          <button
            onClick={() => {
              if (selectedPrize !== null && prizes[selectedPrize].text === "Try Again") {
                startSpin();
              } else {
                setShowForm(true);
              }
            }}
            disabled={spinning}
            className="mt-10 bg-blue-600 text-white px-12 py-5 rounded-2xl font-bold text-2xl shadow-2xl hover:bg-blue-500 transition-all disabled:opacity-50"
          >
            {spinning ? "🎡 Spinning..." : "🎯 Spin the Wheel"}
          </button>
        )}
      </div>

      {/* FORM MODAL */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div className="bg-gray-900 border border-blue-700 rounded-3xl p-8 w-full max-w-[400px] flex flex-col gap-5 shadow-2xl relative">
            {/* ✅ X close button — closes without spinning */}
            <button
              onClick={() => setShowForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors text-xl font-bold leading-none"
              aria-label="Close"
            >
              ✕
            </button>

            <h3 className="text-2xl font-bold text-center text-blue-400 mb-4">
              🎊 Enter Your Details
            </h3>

            {/* Name */}
            <div className="flex flex-col gap-2">
              <input
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={`bg-black border p-4 rounded-xl focus:outline-none transition-all placeholder-gray-400 text-white
                  ${formErrors.name ? "border-red-500 focus:ring-2 focus:ring-red-500" : "border-blue-600 focus:ring-2 focus:ring-blue-500"}`}
              />
              {formErrors.name && <p className="text-red-500 text-sm">{formErrors.name}</p>}
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className={`bg-black border p-4 rounded-xl focus:outline-none transition-all placeholder-gray-400 text-white
                  ${formErrors.email ? "border-red-500 focus:ring-2 focus:ring-red-500" : "border-blue-600 focus:ring-2 focus:ring-blue-500"}`}
              />
              {formErrors.email && <p className="text-red-500 text-sm">{formErrors.email}</p>}
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-2">
              <input
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className={`bg-black border p-4 rounded-xl focus:outline-none transition-all placeholder-gray-400 text-white
                  ${formErrors.phone ? "border-red-500 focus:ring-2 focus:ring-red-500" : "border-blue-600 focus:ring-2 focus:ring-blue-500"}`}
              />
              {formErrors.phone && <p className="text-red-500 text-sm">{formErrors.phone}</p>}
            </div>

            {/* ✅ Removed disabled check — user can always click submit and see errors */}
            <button
              onClick={handleFormSubmit}
              className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-4 rounded-xl font-bold shadow-lg hover:from-blue-500 hover:to-blue-700 transition-all"
            >
              ✅ Confirm & Spin
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ClassicSpinWheel;